/**
 * Main entrypoint for our application.
 *
 * Note on imports:
 * ----------------
 *  Alias paths are defined in `tsconfig.json` and `vite.config.ts`.
 */

// Analytics - must be loaded early to capture all events
import '@/analytics/setup';

// CSS
import '@/stylesheets/application.scss';

// JS Components
// Import all of bootstrap js so we can use data attributes.
import 'bootstrap';

import '@/common/disable-form-submissions.js';
import '@/common/table-toggle-rows.js';
import '@/common/confirmation-modal.js';
import '@/common/checkbox-grid.js';
import '@/common/tooltips.js';
import '@/common/toggle_dollar_percentage';
import '@/common/setup-modal-focus';
import '@/common/turboSetup.js';

import '@/controllers';

// Lazy-load page-specific modules only when their DOM markers appear on the page.
// import.meta.glob with eager:false produces lazy loaders without forcing TS to
// resolve declaration files for each .js module. `import()` is idempotent, so
// re-running the loop on turbo:load is safe.
const moduleLoaders = import.meta.glob<unknown>(
  [
    '@/application/mask_inputs.js',
    '@/application/run_payroll/index.js',
    '@/application/date-format.js',
    '@/application/contractor_info_form.js',
    '@/application/contractor_new_hire_report.js',
    '@/application/employee_bank_account.js',
    '@/application/employee_onboarding_documents_configs.js',
    '@/application/employee_i9_authorizations.js',
    '@/application/employee_i9_authorization_documents.js',
    '@/application/forms.js',
    '@/application/contractor_payments/edit_compensations.js',
    '@/application/contractor_payments/control_display.js',
  ],
  { eager: false },
);

function loaderFor(modulePath: string): () => Promise<unknown> {
  // import.meta.glob keys are paths relative to the Vite root (app/frontend).
  const fn = moduleLoaders[modulePath];
  if (!fn) {
    throw new Error(
      `Missing lazy loader for ${modulePath}. Available: ${Object.keys(moduleLoaders).join(', ')}`,
    );
  }
  return fn;
}

const lazyApplicationModules: ReadonlyArray<readonly [string, () => Promise<unknown>]> = [
  [
    '[data-mask], [data-mask-ssn], [data-mask-ein], [data-mask-routing-number], [data-mask-bank-account-number], [data-mask-currency], [data-mask-wc-rate], [data-mask-hours], [data-mask-hours-negative], [data-mask-days], [data-mask-hexcolor]',
    // mask_inputs is loaded across multiple unrelated pages. The module's
    // top-level setup only runs on its first import, so a later turbo:load
    // tick must re-invoke setupMasks() to re-attach imask to inputs that
    // weren't in the DOM the first time (EMBC-1288).
    () =>
      loaderFor('/application/mask_inputs.js')().then((mod) => {
        (mod as { setupMasks?: () => void }).setupMasks?.();
        return mod;
      }),
  ],
  [
    '#run-payroll-form, #run-payroll-submit, #cancel-modal-button, .submit-payroll-form, #payroll-checks-form',
    loaderFor('/application/run_payroll/index.js'),
  ],
  [
    '.convert-to-local-datetime, .convert-to-local-datetime-with-year, .convert-to-local-time',
    loaderFor('/application/date-format.js'),
  ],
  [
    '.js_contractor_type, #contractor_self_onboarding',
    loaderFor('/application/contractor_info_form.js'),
  ],
  [
    '#contractor_file_new_hire_report_true',
    loaderFor('/application/contractor_new_hire_report.js'),
  ],
  [
    'input[name="employee_payment_method[type]"]',
    loaderFor('/application/employee_bank_account.js'),
  ],
  [
    '#employee_onboarding_documents_config_i9_document',
    loaderFor('/application/employee_onboarding_documents_configs.js'),
  ],
  [
    '#employee_i9_authorization_authorization_status',
    loaderFor('/application/employee_i9_authorizations.js'),
  ],
  [
    'input[name="document_options[document_type_A]"], input[name="document_options[document_type_B]"], input[name="document_options[document_type_C]"]',
    loaderFor('/application/employee_i9_authorization_documents.js'),
  ],
  ['input[name="form[preparer]"]', loaderFor('/application/forms.js')],
  [
    '#contractor-compensation-modal',
    loaderFor('/application/contractor_payments/edit_compensations.js'),
  ],
  [
    '#save-payments, #edit-payments',
    loaderFor('/application/contractor_payments/control_display.js'),
  ],
];

function loadLazyApplicationModulesForCurrentPage() {
  for (const [selector, load] of lazyApplicationModules) {
    if (document.querySelector(selector)) {
      load();
    }
  }
}

loadLazyApplicationModulesForCurrentPage();
document.addEventListener('turbo:load', loadLazyApplicationModulesForCurrentPage);
